import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography, Box, CircularProgress, Stack, Divider, Chip } from '@mui/material';
import {
  TrendingUp,
  Group as PeopleIcon,
  Error as AlertIcon,
  Timer as TimerIcon,
  AutoGraph as ActivityIcon
} from '@mui/icons-material';
import { collection, onSnapshot, query, limit, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useTenant } from '../contexts/TenantContext';
import apiClient from '../api/client';
import { DashboardMetrics, Lead } from '../types';

const MetricCard: React.FC<{ title: string, value: string | number, icon: React.ReactNode, color: string }> = ({ title, value, icon, color }) => (
  <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15`, color }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{title}</Typography>
      <Typography variant="h4" fontWeight="bold">{value}</Typography>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const { organizationId, workspaceId } = useTenant();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await apiClient.get<DashboardMetrics>('/api/v1/dashboard/metrics');
        setMetrics(response.data);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      }
    };

    fetchMetrics();
  }, []);

  useEffect(() => {
    if (!organizationId || !workspaceId) return;

    const leadsRef = collection(db, 'organizations', organizationId, 'workspaces', workspaceId, 'leads');
    const q = query(leadsRef, orderBy('createdAt', 'desc'), limit(5));

    const unsubscribe = onSnapshot(q, (snapshot: any) => {
      const leadsData: Lead[] = [];
      snapshot.forEach((doc: any) => {
        leadsData.push({ id: doc.id, ...doc.data() } as Lead);
      });
      setRecentLeads(leadsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [organizationId, workspaceId]);

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>Executive Overview</Typography>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="New Leads" value={metrics?.newLeads || 0} icon={<PeopleIcon />} color="#2196f3" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Conversion" value={metrics?.conversionRate || '0%'} icon={<TrendingUp />} color="#4caf50" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="SLA Health" value={metrics?.slaCompliance || '0%'} icon={<TimerIcon />} color="#ff9800" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Overdue" value={metrics?.overdueFollowups || 0} icon={<AlertIcon />} color="#f44336" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <ActivityIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Recent Inbound Leads</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              {recentLeads.map((lead) => (
                <Box key={lead.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{lead.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{lead.source} • {new Date(lead.createdAt).toLocaleString()}</Typography>
                  </Box>
                  <Chip label={lead.stage} size="small" variant="outlined" color="primary" />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Pipeline Distribution */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Pipeline Health</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>Distribution of leads across sales stages</Typography>

            <Stack spacing={3}>
              {["New", "Contacted", "Qualified", "Won"].map((stage) => {
                const count = recentLeads.filter(l => l.stage === stage).length;
                return (
                  <Box key={stage}>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{stage}</Typography>
                      <Typography variant="body2" fontWeight="bold">{count}</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', bgcolor: 'primary.main', width: `${(count / (recentLeads.length || 1)) * 100}%` }} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
